import type { GridApi, GridState } from 'ag-grid-community';

export type TablePersistedState = 'full' | 'columnLayout';

export const resolvePersistedGridState = ({
  state,
  persistedState,
}: {
  state: GridState;
  persistedState: TablePersistedState;
}): GridState =>
  persistedState === 'columnLayout'
    ? {
        columnOrder: state.columnOrder,
        columnSizing: state.columnSizing,
        partialColumnState: true,
      }
    : state;

export function applyGridStateToApi({
  api,
  state,
  persistedState,
}: {
  api: GridApi;
  state: GridState;
  persistedState: TablePersistedState;
}) {
  const orderedColIds = state.columnOrder?.orderedColIds;

  if (!orderedColIds?.length) return;
  const sizingMap = new Map(
    state.columnSizing?.columnSizingModel?.map((s) => [s.colId, s]),
  );
  const hiddenSet = new Set(state.columnVisibility?.hiddenColIds);
  const leftPinned = new Set(state.columnPinning?.leftColIds);
  const rightPinned = new Set(state.columnPinning?.rightColIds);
  const sortMap = new Map(
    state.sort?.sortModel?.map((s, idx) => [
      s.colId,
      { sort: s.sort, sortIndex: idx },
    ]),
  );
  const isFullState = persistedState === 'full';
  const columnStates = orderedColIds.map((colId) => {
    const sizing = sizingMap.get(colId);
    const sortEntry = sortMap.get(colId);

    return {
      colId,
      ...(sizing?.width != null && { width: sizing.width }),
      ...(sizing?.flex != null && { flex: sizing.flex }),
      ...(isFullState && {
        hide: hiddenSet.has(colId),
        pinned: leftPinned.has(colId)
          ? ('left' as const)
          : rightPinned.has(colId)
            ? ('right' as const)
            : null,
      }),
      ...(sortEntry && {
        sort: sortEntry.sort,
        sortIndex: sortEntry.sortIndex,
      }),
    };
  });

  api.applyColumnState({ state: columnStates, applyOrder: true });
  if (isFullState && state.filter?.filterModel) {
    api.setFilterModel(state.filter.filterModel);
  }
}
